<template>
  <div
    class="play-form-group"
    :class="{ 'is-invalid': state === false, 'is-valid': state === true }"
    role="group"
    :aria-labelledby="label ? labelId : null"
  >
    <label
      v-if="label || $slots.label"
      :id="labelId"
      class="play-form-group__label"
      :for="labelFor || null"
    >
      <slot name="label">{{ label }}</slot>
      <span
        v-if="required"
        class="play-form-group__req"
        aria-hidden="true"
      >*</span>
    </label>

    <div class="play-form-group__control">
      <slot />
    </div>

    <p
      v-if="state === false && (invalidFeedback || $slots.invalid)"
      class="play-form-group__msg play-form-group__msg--error"
    >
      <slot name="invalid">
        {{ invalidFeedback }}
      </slot>
    </p>
    <p
      v-else-if="description || $slots.description"
      class="play-form-group__msg play-form-group__msg--hint"
    >
      <slot name="description">
        {{ description }}
      </slot>
    </p>
  </div>
</template>

<script>
/**
 * PlayFormGroup — labelled field wrapper for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-form-group> as a generic wrapper around ANY control (slotted): inputs,
 * radio groups, phone fields, etc. (For a plain text field, <PlayInput> already
 * bundles label + field + error — reach for this when wrapping other controls.)
 *
 * `label` + `labelFor` associate the label with the control; `state` (true |
 * false | null) drives valid/invalid styling, with `invalidFeedback` shown when
 * `state === false`; `description` is neutral helper text. Slots `label`,
 * `default`, `invalid`, `description` cover custom content.
 *
 * VUE3-MIGRATION: nothing Vue 2-specific — already Vue 3-safe.
 */
let uid = 0

export default {
  name: 'PlayFormGroup',
  props: {
    label: {
      type: String,
      default: '',
    },
    /** id of the control this label points at */
    labelFor: {
      type: String,
      default: '',
    },
    /** true = valid, false = invalid, null = neutral */
    state: {
      type: Boolean,
      default: null,
    },
    invalidFeedback: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    uid += 1
    return {
      labelId: `play-fg-label-${uid}`,
    }
  },
}
</script>

<style lang="scss" scoped>
.play-form-group {
  display: block;
  margin-bottom: 18px;

  &__label {
    display: block;
    margin-bottom: 8px;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 14px;
    color: var(--text-strong);
  }

  &__req { color: var(--c-danger); }

  &__msg {
    margin: 6px 0 0;
    font-family: var(--font-body);
    font-size: 12.5px;
    font-weight: 500;

    &--error { color: var(--c-danger-strong); }
    &--hint { color: var(--text-muted); }
  }
}
</style>
